import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { formatCnpj, isValidCpf, maskCpf, maskPhone } from '../../../../../domain';
import { CompanyApi } from '../companies.api';
import { CompaniesKeys } from '../companies.key';
import { CompanyEditKeys } from './company-edit.key';
import { CompanyEditScope, SegmentOptionScope } from './company-edit.scope';
/** O CNPJ que o backend exige: `00.000.000/0000-00`, sem folga. */
export const CNPJ_PATTERN = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
const LOG = Logger.get('Mormaii.CompanyEditPresenter');
// @Inject
const companyApi = CompanyApi.INSTANCE;
/**
 * O dialogo de cadastro e edicao de empresa.
 *
 * Um dialogo aberto E um lugar onde o leitor esta: `#/hub/companies/edit?empresa=5`
 * abre o formulario daquela empresa, um reload o mantem aberto, e o Voltar o fecha -
 * sem ninguem escrever tratamento de Escape nem de clique fora.
 *
 * O que o formulario tem digitado NAO vai para a URL, e isso e deliberado: um
 * rascunho nao e um lugar, e um endereco que muda a cada tecla encheria o historico
 * e faria o Voltar apagar letra por letra.
 */
export class CompanyEditPresenter extends CubePresenter {
    constructor(app) {
        super(app, new CompanyEditScope());
        this.dialogSlot = NOOP_VOID;
        /** O segmento escolhido no formulario, e o que esta GRAVADO - ver `segmentClearable`. */
        this.segmentId = null;
        this.savedSegmentId = null;
    }
    async applyParameters(intent, initialization) {
        const keys = new CompanyEditKeys(this.app, intent);
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.list = keys.list;
        if (initialization) {
            this.scope.onCnpjChanged = this.handleCnpjChanged.bind(this);
            this.scope.onRazaoSocialChanged = this.handleChanged.bind(this, 'razaoSocial');
            this.scope.onNomeFantasiaChanged = this.handleChanged.bind(this, 'nomeFantasia');
            this.scope.onRamoAtividadeChanged = this.handleChanged.bind(this, 'ramoAtividade');
            this.scope.onContactNameChanged = this.handleChanged.bind(this, 'contactName');
            this.scope.onContactCpfChanged = this.handleCpfChanged.bind(this);
            this.scope.onContactPhoneChanged = this.handlePhoneChanged.bind(this);
            this.scope.onContactEmailChanged = this.handleChanged.bind(this, 'contactEmail');
            this.scope.onContractedHeadcountChanged = this.handleHeadcountChanged.bind(this);
            this.scope.onClearSegment = this.action(this.onClearSegment);
            this.scope.onSave = this.action(this.onSave);
            this.scope.onClose = this.action(this.onClose);
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.dialogSlot = keys.dialogSlot;
        this.companyId = keys.target;
        this.scope.creating = this.companyId === undefined;
        this.scope.icpEditable = keys.owner?.allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']) ?? false;
        // Titulos como os de la, com maiuscula em Empresa.
        this.scope.title = this.scope.creating ? 'Cadastrar Empresa' : 'Editar Empresa';
        if (this.scope.creating) {
            // O ramo comeca preenchido e NAO tem campo no cadastro: na referencia o
            // seletor esta comentado, e o `initialFormState` manda este valor.
            this.scope.ramoAtividade = 'Manutenção Geral';
        }
        if (this.companyId !== undefined) {
            /*
             * A empresa vem da lista quando ela esta na PAGINA em mãos. Desde que a
             * lista e paginada, nem sempre esta: chegando por link a uma empresa de
             * outra pagina, o formulario abria VAZIO e com "Salvar" ativo (B-40,
             * 16/10/2026). Fora da pagina, busca-se a empresa - em paralelo, sem
             * segurar a viagem.
             */
            const company = this.list?.companyOf(this.companyId);
            if (company) {
                this.fill(company);
            }
            else {
                this.fetchAndFill(this.companyId).catch((caught) => this.app.unexpected('Carregando a empresa', caught));
            }
        }
        if (this.scope.icpEditable) {
            if (initialization) {
                await this.loadSegments();
            }
            else {
                this.markSegment();
            }
        }
        // O formulario acabou de ser preenchido pelo presenter: os campos nao
        // controlados precisam remontar para mostrar o que chegou.
        this.scope.formRevision++;
        this.dialogSlot(this.scope);
        return true;
    }
    fill(company) {
        this.scope.cnpj = company.cnpj ?? '';
        this.scope.razaoSocial = company.razaoSocial ?? '';
        this.scope.nomeFantasia = company.nomeFantasia ?? '';
        this.scope.ramoAtividade = company.ramoAtividade ?? '';
        this.scope.contactName = company.contactName ?? '';
        this.scope.contactPhone = company.contactPhone ?? '';
        this.scope.contactEmail = company.contactEmail ?? '';
        // O segmento e o porte contratado do ICP (so o administrador geral os edita).
        this.segmentId = company.segmentId ?? null;
        this.savedSegmentId = this.segmentId;
        this.scope.segmentSummary = company.segmentLabel ?? '';
        this.scope.contractedHeadcount = company.contractedHeadcount ? String(company.contractedHeadcount) : '';
    }
    /**
     * A empresa que nao esta na pagina da lista.
     *
     * O formulario fica ocupado enquanto ela nao chega, para ninguem salvar campos
     * vazios por cima dela. Se ela nao existe mais - o link e velho, ou veio de outra
     * base -, o dialogo avisa e fecha, voltando para a lista.
     */
    async fetchAndFill(companyId) {
        this.scope.loading = true;
        try {
            const company = await companyApi.fetchCompany(companyId);
            if (this.companyId !== companyId) {
                return;
            }
            this.fill(company);
            if (this.scope.icpEditable) {
                this.markSegment();
            }
            this.scope.formRevision++;
        }
        catch (caught) {
            if (this.companyId !== companyId) {
                return;
            }
            LOG.warn('Could not load the company', caught);
            this.app.alert('error', 'Empresa não encontrada', 'O link aponta para uma empresa que não existe mais.');
            await this.close();
        }
        finally {
            if (this.companyId === companyId) {
                this.scope.loading = false;
            }
        }
    }
    /**
     * O catalogo de segmentos, buscado UMA vez por abertura do dialogo.
     *
     * Opcional no formulario: sem ele o cadastro continua possivel, e derrubar o dialogo
     * por causa do catalogo seria desproporcional - a mesma decisao das profissoes no
     * formulario de usuarios da empresa.
     */
    async loadSegments() {
        try {
            this.showSegments(await companyApi.fetchSegments());
        }
        catch (caught) {
            LOG.warn('Could not load the company segments', caught);
        }
    }
    /**
     * A lista do seletor, montada uma vez; escolher marca a opcao no lugar.
     *
     * Refazer o array durante o `onChange` do seletor realimenta a mudanca - ver
     * `CompanyUserFormPresenter.showTypes`, onde isso estourou a pilha.
     */
    showSegments(catalog) {
        this.scope.segments = catalog.map((segment) => {
            const scope = new SegmentOptionScope();
            scope.identity = String(segment.id);
            scope.label = segment.label;
            scope.value = String(segment.id);
            scope.current = segment.id === this.segmentId;
            scope.onSelect = this.action(this.onChooseSegment.bind(this, segment.id));
            scope.update = this.update;
            return scope;
        });
        this.markSegment();
    }
    markSegment() {
        let summary = '';
        for (const option of this.scope.segments) {
            option.current = option.value === String(this.segmentId);
            summary = option.current ? option.label : summary;
        }
        // Sem o catalogo em maos, o nome que veio com a empresa.
        this.scope.segmentSummary = summary || (this.segmentId === null ? '' : this.scope.segmentSummary);
        this.scope.segmentClearable = this.savedSegmentId === null;
    }
    async onChooseSegment(segmentId) {
        this.segmentId = segmentId;
        this.markSegment();
    }
    /** So enquanto nao ha segmento gravado: desfaz uma escolha que ainda nao foi salva. */
    async onClearSegment() {
        this.segmentId = null;
        this.markSegment();
    }
    /**
     * O que o endereco diz enquanto o formulario esta aberto.
     *
     * So o alvo. O que esta digitado NAO vai para a URL: um rascunho nao e um lugar,
     * e um endereco que muda a cada tecla encheria o historico.
     */
    publishParameters(intent) {
        const keys = new CompanyEditKeys(this.app, intent);
        keys.target = this.companyId;
    }
    // Espelham o digitado. Nao sao acoes: guardar o rascunho nao e uma operacao que
    // possa falhar, e nao deve escrever no historico.
    handleChanged(field, value) {
        this.scope[field] = value;
    }
    handleCpfChanged(value) {
        this.scope.contactCpf = maskCpf(value);
        this.scope.contactCpfError = undefined;
    }
    handleCnpjChanged(value) {
        this.scope.cnpj = formatCnpj(value);
    }
    handlePhoneChanged(value) {
        this.scope.contactPhone = maskPhone(value);
    }
    /** So digitos: colaboradores contratados e uma contagem. */
    handleHeadcountChanged(value) {
        this.scope.contractedHeadcount = value.replace(/\D/g, '');
        this.scope.contractedHeadcountError = undefined;
    }
    /**
     * Valida o que o backend exige, antes de gastar uma requisicao.
     *
     * As regras sao as do `CompanyDtoRequest`: CNPJ na mascara exata, razao social,
     * nome, telefone e e-mail do contato obrigatorios. Validar aqui nao substitui a
     * validacao do servidor - ela continua sendo a que vale - mas evita o percurso
     * inteiro para um campo vazio.
     */
    validate() {
        this.scope.cnpjError = undefined;
        this.scope.razaoSocialError = undefined;
        this.scope.contactNameError = undefined;
        this.scope.contactCpfError = undefined;
        this.scope.contactPhoneError = undefined;
        this.scope.contactEmailError = undefined;
        this.scope.contractedHeadcountError = undefined;
        let valid = true;
        // Vale nos dois: o update passou a aceitar CNPJ, e o backend confere
        // formato e disponibilidade antes de gravar.
        if (!CNPJ_PATTERN.test(this.scope.cnpj)) {
            this.scope.cnpjError = 'Informe no formato 00.000.000/0000-00.';
            valid = false;
        }
        if (!this.scope.razaoSocial.trim()) {
            this.scope.razaoSocialError = 'Obrigatório.';
            valid = false;
        }
        if (!this.scope.contactName.trim()) {
            this.scope.contactNameError = 'Obrigatório.';
            valid = false;
        }
        // O CPF e OPCIONAL - em branco, o servidor fabrica um a partir do CNPJ. Mas
        // preenchido ele tem de ser um CPF de verdade: um invalido aqui vira a
        // identidade do administrador da empresa.
        if (this.scope.contactCpf.trim() && !isValidCpf(this.scope.contactCpf)) {
            this.scope.contactCpfError = 'CPF inválido.';
            valid = false;
        }
        if (!this.scope.contactPhone.trim()) {
            this.scope.contactPhoneError = 'Obrigatório.';
            valid = false;
        }
        const email = this.scope.contactEmail.trim();
        if (!email) {
            this.scope.contactEmailError = 'Obrigatório.';
            valid = false;
        }
        else if (!email.includes('@')) {
            this.scope.contactEmailError = 'E-mail inválido.';
            valid = false;
        }
        // Em branco nao altera; preenchido, o servidor exige maior que zero.
        if (this.scope.icpEditable && this.scope.contractedHeadcount && Number(this.scope.contractedHeadcount) < 1) {
            this.scope.contractedHeadcountError = 'Informe a partir de 1.';
            valid = false;
        }
        return valid;
    }
    async onSave() {
        if (!this.validate()) {
            // Nada de falhar em silencio: a referencia deixa a validacao para o
            // navegador (`required`, `type=email`), e o que sobra aqui - a mascara
            // do CNPJ e o e-mail sem arroba - vira aviso, como os erros de servidor.
            const first = this.scope.cnpjError ??
                this.scope.razaoSocialError ??
                this.scope.contactNameError ??
                this.scope.contactCpfError ??
                this.scope.contactPhoneError ??
                this.scope.contactEmailError ??
                this.scope.contractedHeadcountError;
            this.app.alert('error', 'Erro', first ?? 'Revise os dados informados.');
            return;
        }
        const input = {
            cnpj: this.scope.cnpj.trim(),
            razaoSocial: this.scope.razaoSocial.trim(),
            nomeFantasia: this.scope.nomeFantasia.trim() || undefined,
            ramoAtividade: this.scope.ramoAtividade.trim() || undefined,
            contactName: this.scope.contactName.trim(),
            // So na CRIACAO: e o CPF do administrador que nasce junto, e depois
            // dele nascido quem corrige e a area de usuarios da empresa.
            contactCpf: this.companyId === undefined ? this.scope.contactCpf.trim() || undefined : undefined,
            contactPhone: this.scope.contactPhone.trim(),
            contactEmail: this.scope.contactEmail.trim(),
            // So o administrador geral manda os dois; para os outros papeis eles nem existem.
            ...(this.scope.icpEditable
                ? {
                    segmentId: this.segmentId,
                    contractedHeadcount: this.scope.contractedHeadcount
                        ? Number(this.scope.contractedHeadcount)
                        : null
                }
                : {})
        };
        const criando = this.companyId === undefined;
        this.scope.busy = true;
        try {
            if (criando) {
                await companyApi.createCompany(input);
            }
            else {
                await companyApi.updateCompany(this.companyId, input);
            }
            // Quem mudou o dado avisa quem o mostra, e so depois fecha - fechar antes
            // deixaria a lista mostrando o estado anterior por um instante.
            await this.list?.reload();
            // O cadastro avisa, a edicao so fecha - e o que a referencia faz.
            if (criando) {
                this.app.alert('info', 'Sucesso', 'Empresa criada com sucesso!');
            }
            await this.close();
        }
        catch (caught) {
            // Mensagens de la: o cadastro mostra a do servidor, a edicao tem texto
            // proprio.
            if (criando) {
                this.app.alert('error', 'Erro', caught instanceof Error ? caught.message : 'Erro ao criar empresa');
            }
            else {
                this.app.alert('error', 'Erro ao editar empresa', 'Tente novamente mais tarde.');
            }
        }
        finally {
            this.scope.busy = false;
        }
    }
    async onClose() {
        await this.close();
    }
    /**
     * Fecha o dialogo voltando para a lista, com o filtro que ela tem.
     *
     * O intent tem de ser NOVO, e nao o do dialogo vestido de `CompaniesKeys`: uma
     * classe de keys so usa o proprio place ao CRIAR um intent, entao envolver o do
     * dialogo daria um flip para o proprio dialogo. `CompaniesKeys.of` cria esse
     * intent novo e o framework o preenche perguntando aos presenters vivos -
     * incluindo o da lista, que e o place pai deste.
     */
    async close() {
        await CompaniesKeys.of(this.app).flip();
    }
}
//# sourceMappingURL=company-edit.presenter.js.map